<template>
  <material-card
    :title="title"
    data-testId="material-card-title"
    color="dover-blue">
    <v-form
      ref="form"
      lazy-validation>
      <v-container
        py-0>
        <v-layout
          wrap>
          <template v-if="!isTechnicianUser">
            <v-flex
              xs12
              md12
              lg12>
              <span data-testId="span-progressBar-label">{{ progressBar.label }}</span>
              <MaglinkComponentLoadingBar
                :loading="progressBar.loading"
                :indeterminate="false"
                :max-value="progressBar.maxValue"
                :value="progressBar.currentValue"
                data-testId="MaglinkComponentLoadingBar" />
            </v-flex>
            <v-flex
              xs12
              md12
              lg12>
              <span
                data-testId="span-Update-console"
                class="caption grey--text font-weight-bold">
                {{ $t('Update console') }}
              </span>
              <hr>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <v-flex
                  xs12
                  md12
                  lg12>
                  <v-text-field
                    v-model="update.filename"
                    :label="`${ $t('Select latest version to upload for the update') }`"
                    :disabled="!enableActions"
                    data-testId="text-field-Select-latest-version"
                    prepend-icon="mdi-attachment"/>
                  <input
                    ref="updateFile"
                    :disabled="!enableActions"
                    data-testId="input-update"
                    type="file"
                    style="display: none"
                    accept="zip"
                    @change="onUpdateFilePicked">
                </v-flex>
                <v-flex
                  xs12
                  md12
                  lg12
                  text-xs-right>
                  <v-text-field
                    v-model="uploadedFile"
                    :label="`${ $t('File Size') }`"
                    data-testId="text-field-File-Size"
                    disabled
                    class="dover-input"/>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions || isTechnicianUser"
                    data-testId="btn-Select-zip-file"
                    block
                    small
                    color="dover-blue"
                    @click="doPickRemoteUpdateFile">
                    {{ $t('Select zip file') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!update.rebootEnabled"
                    data-testId="btn-Update"
                    block
                    small
                    color="dover-blue"
                    @click="doConsoleReboot">
                    {{ $t('Update') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
          </template>
          <template v-if="!isTechnicianUser">
            <v-flex
              xs12
              md12
              lg12>
              <span
                data-testId="span-Database-backup"
                class="caption grey--text font-weight-bold">
                {{ $t('Database backup') }}
              </span>
              <hr>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6
              pt-3>
              <v-combobox
                v-model="backup.file"
                :items="backup.available"
                :disabled="!enableActions"
                data-testId="combobox-backup"
                item-text="name"
                item-value="name"
                chips
                solo
                outline
                dense >
                <template
                  slot="selection"
                  slot-scope="data">
                  <v-chip
                    :selected="data.selected"
                    data-testId="chip-name">
                    <strong>{{ data.item.name }}</strong>&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions"
                    data-testId="btn-Create-new-backup"
                    block
                    small
                    color="dover-blue"
                    @click="doBackup">
                    {{ $t('Create new backup') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions || (missingBackup)"
                    data-testId="btn-Download-backup-to-PC"
                    block
                    small
                    color="dover-blue"
                    @click="doDownloadBackup">
                    {{ $t('Download backup to PC') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions || (missingBackup)"
                    data-testId="btn-Restore-selected-backup"
                    block
                    small
                    color="dover-blue"
                    @click="doRestoreLocaleDatabase">
                    {{ $t('Restore selected backup') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions || (missingBackup)"
                    data-testId="btn-Delete-selected-backup"
                    block
                    small
                    color="dover-blue"
                    @click="doDeleteBackup">
                    {{ $t('Delete selected backup') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
          </template>
          <template v-if="!isTechnicianUser">
            <v-flex
              xs12
              md12
              lg12>
              <span
                data-testId="span-Upload-backup"
                class="caption grey--text font-weight-bold">
                {{ $t('Upload backup') }}
              </span>
              <hr>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <v-flex
                  xs12
                  md12
                  lg12>
                  <v-text-field
                    v-model="database.filename"
                    :label="`${ $t('Select Database backup [data_yyyy-mm-dd__hh-mm-ss.zip]') }`"
                    :disabled="!enableActions"
                    data-testId="text-field-Select-Database-backup"
                    prepend-icon="mdi-attachment"/>
                  <input
                    ref="databaseBackupFile"
                    :disabled="!enableActions"
                    data-testId="input-backup-pick"
                    type="file"
                    style="display: none"
                    accept="zip"
                    @change="onDatabaseBackupFilePicked">
                </v-flex>
                <v-flex
                  xs12
                  md12
                  lg12>
                  <v-text-field
                    v-model="itouch.filename"
                    :label="`${ $t('Select Configuration backup [temp_data_yyyy-mm-dd__hh-mm-ss.zip]') }`"
                    :disabled="!enableActions"
                    data-testId="text-field-Select-Configuration-backup"
                    prepend-icon="mdi-attachment"/>
                  <input
                    ref="itouchBackupFile"
                    :disabled="!enableActions"
                    data-testId="input-itouch-pick"
                    type="file"
                    style="display: none"
                    accept="zip"
                    @change="onITouchBackupFilePicked">
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions"
                    data-testId="btn-Select-database-backup"
                    block
                    small
                    color="dover-blue"
                    @click="doPickDatabaseBackupFile">
                    {{ $t('Select database backup') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!backupRestorable"
                    data-testId="btn-Restore-Backup"
                    block
                    small
                    color="dover-blue"
                    @click="onBackupRestore">
                    {{ $t('Restore Backup') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions"
                    data-testId="btn-Select-configuration-backup"
                    block
                    small
                    color="dover-blue"
                    @click="doPickITouchBackupFile">
                    {{ $t('Select configuration backup') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs6
                  md6
                  lg6 />
              </v-layout>
            </v-flex>
          </template>
          <template>
            <v-flex
              xs12
              md12
              lg12>
              <span
                data-testId="span-Log-files"
                class="caption grey--text font-weight-bold">
                {{ $t('Log files') }}
              </span>
              <hr>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6
              pt-3>
              <v-combobox
                v-model="log.file"
                :items="log.available"
                :disabled="!enableActions"
                data-testId="combobox-log"
                item-text="name"
                item-value="name"
                chips
                solo
                outline
                dense >
                <template
                  slot="selection"
                  slot-scope="data">
                  <v-chip
                    :selected="data.selected"
                    data-testId="chip-log">
                    <strong>{{ data.item.name }}</strong>&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-flex>
            <v-flex
              xs12
              md3
              lg3
              text-xs-center
              px-2>
              <v-btn
                :disabled="!enableActions || (missingLogfile)"
                data-testId="btn-Download-log-file"
                block
                small
                color="dover-blue"
                @click="doDownloadLogFiles">
                {{ $t('Download log file') }}
              </v-btn>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-text-field
                v-model="loglevel.filename"
                :label="`${ $t('Select log level') }`"
                :disabled="!enableActions"
                data-testId="text-field-Select-log-level"
                prepend-icon="mdi-attachment"/>
              <input
                ref="loglevelFile"
                :disabled="!enableActions"
                data-testId="input-level"
                type="file"
                style="display: none"
                accept="txt"
                @change="onLoglevelFilePicked">
            </v-flex>
            <v-flex
              xs6
              md3
              lg3
              text-xs-center
              px-2>
              <v-btn
                :disabled="!enableActions"
                data-testId="btn-Select-log-level"
                block
                small
                color="dover-blue"
                @click="doPickLoglevelFile">
                {{ $t('Select log level') }}
              </v-btn>
            </v-flex>
            <v-flex
              xs6
              md3
              lg3 />
          </template>
          <template>
            <v-flex
              xs12
              md12
              lg12>
              <span
                data-testId="span-Upload-license"
                class="caption grey--text font-weight-bold">
                {{ $t('Upload license') }}
              </span>
              <hr>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <v-flex
                  xs12
                  md12
                  lg12>
                  <v-text-field
                    v-model="license.filename"
                    :label="`${ $t('Select license file') }`"
                    :disabled="!enableActions"
                    data-testId="text-field-Select-license-file"
                    prepend-icon="mdi-attachment"/>
                  <input
                    ref="licenseFile"
                    :disabled="!enableActions"
                    data-testId="input-license"
                    type="file"
                    style="display: none"
                    accept="ssr"
                    @change="onLicenseFilePicked">
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions"
                    data-testId="btn-Select-license-file"
                    block
                    small
                    color="dover-blue"
                    @click="doPickLicenseFile">
                    {{ $t('Select license file') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-text-field
                    v-model="featureSettings.serialNumber"
                    :label="`${ $t('ESN') }`"
                    data-testId="text-field-ESN"
                    readonly
                    class="dover-input"/>
                </v-flex>
                <v-flex
                  v-if="isCsldEnabled"
                  xs12
                  md12
                  lg12
                  text-xs-right
                  px-2>
                  <span data-testId="span-CSLD-Enabled">
                    {{ $t('CSLD Enabled') }}
                  </span>
                </v-flex>
              </v-layout>
            </v-flex>
          </template>
          <MaglinkComponentDialogYesNo
            :key="dialogYN.key"
            :component-key="dialogYN.componentKey"
            :dialog="dialogYN.show"
            :title="dialogYN.title"
            :label-text="dialogYN.labelText"
            :custom-par1="dialogYN.action"
            :custom-par2="dialogYN.result"
            data-testId="MaglinkComponentDialogYesNo"
            @dialogResultYesNo="onDialogResult"/>
          <template>
            <v-flex
              xs12
              md12
              lg12>
              <span
                data-testId="span-Console-reset"
                class="caption grey--text font-weight-bold">
                {{ $t('Console reset') }}
              </span>
              <hr>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    data-testId="btn-Reboot"
                    block
                    small
                    color="dover-blue"
                    @click="doConsoleReboot">
                    {{ $t('Reboot') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex
              xs12
              md6
              lg6>
              <v-layout
                row
                wrap>
                <!-- Soft Reset is disabled because is not managed by Start script
                <v-flex
                  xs6
                  md6
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions"
                    data-testId="btn-Soft-reset"
                    block
                    small
                    color="dover-blue"
                    @click="ConsoleReset('softReset')">
                    {{ $t('Soft reset (keep ONLY site settings)') }}
                  </v-btn>
                </v-flex>
-->
                <v-flex
                  xs12
                  md12
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions"
                    data-testId="btn-Cold-reset"
                    block
                    small
                    color="dover-red"
                    @click="ConsoleReset('factory')">
                    {{ $t('Cold reset (RESET TO FACTORY DEFAULTS)') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs12
                  md12
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions"
                    data-testId="btn-Clear-cache"
                    block
                    small
                    color="dover-red"
                    @click="ConsoleReset('swap')">
                    {{ $t('Clear cache (SOME DATA MIGHT BE LOST)') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  v-if="isCsldEnabled && tanks.length > 0"
                  xs12
                  md12
                  lg6
                  text-xs-center
                  px-2>
                  <v-btn
                    :disabled="!enableActions"
                    data-testId="btn-Clear-CSLD"
                    block
                    small
                    color="dover-red"
                    @click="onCsldResetDialog()">
                    {{ $t('Clear CSLD collected data') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
          </template>
          <v-dialog
            v-model="csldResetDialog"
            data-testId="dialog-csld"
            persistent
            width="600px">
            <v-card data-testId="card">
              <v-card-title
                data-testId="card-title-Warning"
                class="headline grey lighten-2"
                primary-title>
                {{ $t('Warning') }}
              </v-card-title>
              <v-card-text data-testId="card-text">
                <v-container
                  grid-list-md
                  py-0>
                  <v-layout
                    wrap>
                    <v-flex
                      xs6
                      md6>
                      <MaglinkComponentTankList
                        :loading="progressBar.loading"
                        :filters="filters"
                        :tanks="tanks"
                        data-testId="MaglinkComponentTankList" />
                    </v-flex>
                    <v-flex
                      xs6
                      md6>
                      <v-switch
                        v-if="tanks.length > 1"
                        v-model="filters.all"
                        :disabled="progressBar.loading"
                        :label="`${$t('All tanks')}`"
                        data-testId="switch-filters-all"
                        class="dover-input"/>
                    </v-flex>
                    <v-flex
                      xs12
                      md12
                      lg12>
                      <v-textarea
                        v-model="csldResetText"
                        data-testId="textarea-csld-reset"
                        background-color="amber lighten-4"
                        readonly
                        auto-grow
                        full-width
                        filled
                        shaped
                        solo
                        outlined/>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
              <v-divider/>
              <v-card-actions data-testId="card-actions">
                <v-flex
                  xs3
                  md3/>
                <v-flex
                  xs2
                  md2
                  text-xs-right>
                  <v-btn
                    v-if="isCsldEnabled"
                    data-testId="btn-Yes"
                    color="secondary"
                    flat
                    @click="onCloseCsldResetDialog(true)">
                    {{ $t('Yes') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs1
                  md1/>
                <v-flex
                  xs2
                  md2
                  text-xs-center>
                  <v-btn
                    data-testId="btn-No"
                    color="primary"
                    flat
                    @click="onCloseCsldResetDialog(false)">
                    {{ $t('No') }}
                  </v-btn>
                </v-flex>
                <v-flex
                  xs3
                  md3/>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-layout>
      </v-container>
    </v-form>
  </material-card>
</template>

<script>
import Vue from 'vue'

export default {
  props: {
    title: {
      type: String,
      required: true
    },
    tanks: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data: () => ({
    update: {
      filename: '',
      filesize: 0,
      rebootEnabled: false
    },
    database: { filename: '' },
    itouch: { filename: '' },
    loglevel: { filename: '' },
    license: { filename: '' },
    enableActions: false,
    databaseTransfered: false,
    configTransfered: false,
    sourceFunction: '',
    currentOpenCalls: 0,
    featureSettings: { serialNumber: '----' },
    dialogYN: {
      key: 0,
      show: false,
      componentKey: '',
      title: '',
      labelText: '',
      action: '',
      result: ''
    },
    progressBar: {
      loading: false,
      maxValue: 10,
      currentValue: 0,
      tickId: 0,
      label: ''
    },
    log: {
      file: '',
      available: [ ]
    },
    backup: {
      file: '',
      available: [ ]
    },
    csldResetDialog: false,
    filters: {
      all: false,
      number: 1
    },
    isCsldEnabled: false
  }),
  computed: {
    backupRestorable () {
      return (this.database.filename.startsWith('data_') && this.itouch.filename.startsWith('temp_data_') && this.databaseTransfered && this.configTransfered)
    },
    missingBackup () {
      if (this.backup === undefined) return true
      if (this.backup === null) return true
      if (this.backup.file === undefined) return true
      if (this.backup.file === null) return true
      if (this.backup.file === '') return true

      return false
    },
    missingLogfile () {
      if (this.log === undefined) return true
      if (this.log === null) return true
      if (this.log.file === undefined) return true
      if (this.log.file === null) return true
      if (this.log.file === '') return true

      return false
    },
    uploadedFile () {
      return this.Maglink.RoundFormat(this.update.filesize / 1024 / 1024, 2) + ' MB'
    },
    csldResetText () {
      return this.$t('You are clearing CSLD collected data.\nThe data for selected tank will start collecting again, no previous result will be lost.\n Are you sure?')
    },
    isTechnicianUser () {
      return this.Maglink.IsTechnicianUser()
    }
  },
  mounted () {
    this.loadLogList()
    this.loadBackupList()
    this.loadFeatures()
    this.isCsldEnabled = Vue.prototype.Maglink.hasCsld
  },
  beforeDestroy () {
    // Perform the teardown procedure for someLeakyProperty.
    // (In this case, effectively nothing)
    clearInterval(this.progressBar.tickId)
  },
  methods: {
    // ProgressBar Methods
    manageGUI (flag) {
      this.enableActions = flag
    },
    progressBarTick () {
      if (this.progressBar.currentValue >= this.progressBar.maxValue) this.progressBar.currentValue = 0

      this.progressBar.currentValue += 1
      this.progressBar.label = this.$t('Awaiting to complete execution {0}', [ this.progressBar.currentValue ]) + ': ' + this.sourceFunction
    },
    startTimer () {
      // timer already active
      if (this.progressBar.tickId > 0) return

      this.progressBar.label = this.$t('Awaiting to complete execution...')
      this.progressBar.loading = true
      this.progressBar.currentValue = 0
      this.progressBar.tickId = setInterval(this.progressBarTick, 1000)
    },
    stopTimer () {
      if (this.progressBar.tickId === 0) return

      clearInterval(this.progressBar.tickId)
      this.progressBar.tickId = 0
      this.progressBar.loading = false
      this.progressBar.currentValue = 0
      this.progressBar.label = ''
    },
    working (active, source) {
      // to manage concurrent calls
      if (active) {
        this.currentOpenCalls += 1
      } else {
        this.currentOpenCalls -= 1
      }

      if (this.currentOpenCalls > 0) {
        this.manageGUI(false)
        this.sourceFunction = active ? this.$t(source) : ''
        this.startTimer(source)
      } else {
        this.manageGUI(true)
        this.currentOpenCalls = 0
        this.stopTimer()
      }
    },

    // DialogBox Methods
    onDialogResult (componentKey, result, action, par2) {
      this.dialogYN.show = false
      // NO button pressed
      if (!result) return

      if (action === 'deleteBackup') {
        this.doDeleteBackupConfirm(par2)
      }
      if (action === 'factory') {
        this.doColdReset()
      }
      if (action === 'swap') {
        this.doSwapReset()
      }
      if (action === 'softReset') {
        this.doSoftReset()
      }
    },

    // Update Package Methods
    doConsoleReboot () {
      this.Maglink.ConsoleReboot({})
        .then(response => {
          this.snack('success', this.$t('Rebooting...'))
        })
        .catch(error => (this.snack('error', error.message)))
    },
    doPickRemoteUpdateFile () {
      this.$refs.updateFile.click()
    },
    doPickLicenseFile () {
      this.$refs.licenseFile.click()
    },
    onUpdateFilePicked (e) {
      this.working(true, 'Uploading update zip file in progress...')

      this.update.filesize = 0
      this.update.filename = ''
      this.update.rebootEnabled = false

      const files = e.target.files
      if (files.length === 0) return

      if (files[0] !== undefined) {
        let fileToUpload = files[0]
        this.update.filename = fileToUpload.name
        this.update.filesize = fileToUpload.size
        // if you want to upload multiple files at once loop
        // through the array of files

        let formData = new FormData()
        formData.append('file', fileToUpload, fileToUpload.name)

        this.Maglink.ConsoleUpdate(formData)
          .then(response => {
            this.working(false, '')

            let resp = response.data // {result, msg} result=0 --> ok; result != 0 --> "error msg"

            if (resp.result === '0') {
              this.update.rebootEnabled = true
              this.snack('success', this.$t(resp.msg))
            } else {
              this.update.filesize = 0
              this.snack('error', this.$t(resp.msg))
            }
          })
          .catch(error => {
            this.update.filesize = 0
            this.working(false, '')
            this.snack('error', error.message)
          })
      } else {
        this.working(false, '')
        this.snack('error', 'File undefined')
      }
    },
    onLicenseFilePicked (e) {
      const files = e.target.files
      if (files.length === 0) return

      if (files[0] !== undefined) {
        if (/^[^.]+.ssr$/.test(files[0].name) === false) {
          this.snack('error', this.$t('Invalid file format'))
          return
        }

        if (files[0].size !== 44) {
          this.snack('error', this.$t('Invalid file size'))
          return
        }

        this.working(true, 'Applying license file...')
        this.license.filename = files[0].name

        let licenseFile = new FormData()
        licenseFile.append('file', files[0])

        this.Maglink.UploadLicense(licenseFile)
          .then(response => {
            this.working(false, '')

            let resp = response.data

            if (resp.result === '0') {
              setTimeout(this.reloadFeature, 5000, resp.msg)
            } else {
              this.license.filename = ''
              this.snack('error', this.$t(resp.msg))
            }
          })
          .catch(error => {
            this.working(false, '')
            this.snack('error', error.message)
          })
      } else {
        this.working(false, '')
        this.snack('error', 'File undefined')
      }
    },
    reloadFeature (msg) {
      this.Maglink.GetSettings('Features')
        .then(response => {
          this.isCsldEnabled = Vue.prototype.Maglink.checkCsldEnabled(response.data.feature)
          Vue.prototype.Maglink.hasCsld = this.isCsldEnabled
          this.snack('success', this.$t(msg))
        })
        .catch(() => {
          this.snack('error', 'Session timeout')
        })
    },
    // Backup Methods
    doBackup () {
      this.working(true, 'Backup in progress...')

      this.Maglink.CreateNewBackup()
        .then(response => {
          this.working(false, '')

          let resp = response.data

          if (resp.result === '0') {
            this.snack('success', this.$t(resp.msg))
          } else {
            this.snack('error', this.$t(resp.msg))
          }
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    doDeleteBackup () {
      let fn = this.backup.file.name
      this.dialogYN.title = this.$t('Warning').toUpperCase()
      this.dialogYN.labelText = '\n\n' + this.$t('You are about to DELETE selected backup: {0}', [ fn ]) + '\n\n' + this.$t('Are you sure?')
      this.dialogYN.componentKey = 'deleteBackup'
      this.dialogYN.action = 'deleteBackup'
      this.dialogYN.result = fn
      this.dialogYN.show = true
    },
    doDeleteBackupConfirm (fileTodelete) {
      if (fileTodelete === '') return

      var dbName = ''
      var tmpName = ''
      if (this.isDatabaseBackupFile(fileTodelete)) {
        dbName = fileTodelete
        tmpName = 'temp_' + fileTodelete
      } else {
        dbName = fileTodelete.substring(5)
        tmpName = fileTodelete
      }

      this.Maglink.DeleteBackup(dbName)
        .then(response => {
          this.working(false, '')

          let resp = response.data

          if (resp.result === '0') {
            this.backup.file = ''
            this.loadBackupList() // RELOAD BACKUP LIST
            this.snack('success', this.$t(resp.msg))
          } else {
            this.snack('error', this.$t(resp.msg))
          }
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })

      this.Maglink.DeleteBackup(tmpName)
        .then(response => {
          this.working(false, '')

          let resp = response.data

          if (resp.result === '0') {
            this.loadBackupList() // RELOAD BACKUP LIST
            this.snack('success', this.$t(resp.msg))
          } else {
            this.snack('error', this.$t(resp.msg))
          }
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    doDownloadBackup () {
      var filename = this.backup.file.name
      if (filename === '') return

      var dbName = ''
      var tmpName = ''
      if (this.isDatabaseBackupFile(filename)) {
        dbName = filename
        tmpName = 'temp_' + filename
      } else {
        dbName = filename.substring(5)
        tmpName = filename
      }

      this.snack('info', '2 files will be downloaded, BOTH OF THEM ARE REQUIRED')

      this.working(true, 'Database Backup download in progress...')
      this.Maglink.BackupDownload(dbName)
        .then(response => {
          this.working(false, '')

          let fileContent = response.data
          const link = this.Maglink.GetDownloadLinkBinary(fileContent, dbName)
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(error => {
          this.working(false)
          this.snack('error', error.message)
        })

      this.working(true, 'Configuration Backup download in progress...')
      this.Maglink.BackupDownload(tmpName)
        .then(response => {
          this.working(false, '')

          let fileContent = response.data
          const link = this.Maglink.GetDownloadLinkBinary(fileContent, tmpName)
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(error => {
          this.working(false)
          this.snack('error', error.message)
        })
    },
    doPickDatabaseBackupFile () {
      this.$refs.databaseBackupFile.click()
    },
    doPickITouchBackupFile () {
      this.$refs.itouchBackupFile.click()
    },
    doRestoreLocaleDatabase (backupFile) {
      let filename = this.backup.file.name
      if (filename === '') return

      var dbName = ''
      if (this.isDatabaseBackupFile(filename)) {
        dbName = filename
      } else {
        dbName = filename.substring(5)
      }

      this.working(true, 'Restoring local backup in progress...')

      this.Maglink.RestoreLocaleBackup(dbName)
        .then(response => {
          this.working(false, '')

          let resp = response.data // {result, msg} result=0 --> ok; result != 0 --> "error msg"

          this.snack(resp.result === '0' ? 'success' : 'error', resp.msg)
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    isDatabaseBackupFile (value) {
      if (value === undefined) {
        return false
      } else {
        if (value.length <= 4) {
          return false
        } else {
          return !value.includes('temp')
        }
      }
    },
    loadBackupList () {
      this.working(true, 'Loading backup list in progress...')

      this.Maglink.GetBackupList()
        .then(response => {
          this.working(false, '')

          let resp = response.data

          if (resp.result === '0') {
            let sortedList = resp.filenames.filter((a) => { return this.isDatabaseBackupFile(a.name) }).sort((a, b) => (b > a) ? 1 : -1)
            this.backup.available = sortedList
            if (resp.filenames.length === 0) {
              this.backup.file = undefined
            }
          } else {
            this.backup.file = undefined
            this.snack('error', this.$t(resp.msg))
          }
        })
        .catch(error => {
          this.backup.file = undefined
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    loadFeatures () {
      this.working(true, 'Loading feature list in progress...')
      this.Maglink.GetSettings('Features')
        .then(response => {
          this.working(false, '')
          this.featureSettings.serialNumber = response.data.serialNumber
        })
        .catch(() => {
          this.snack('error', 'Session timeout')
        })
    },
    onBackupRestore () {
      this.Maglink.RestoreRemoteBackup()
        .then(response => {
          this.snack('success', this.$t('Rebooting...'))
        })
        .catch(error => (this.snack('error', error.message)))
    },
    onDatabaseBackupFilePicked (e) {
      const files = e.target.files
      if (files.length === 0) return

      if (files[0] !== undefined) {
        if (/^data_\d{4}-\d{2}-\d{2}.{0,}\.zip$/.test(files[0].name) === false) {
          this.snack('error', this.$t('Invalid file format'))
          return
        }

        this.working(true, 'Uploading backup to restore in progress...')
        this.database.filename = files[0].name

        // if you want to upload multiple files at once loop
        // through the array of files
        let zipFile = new FormData()
        zipFile.append('file', files[0])

        this.Maglink.UploadBackupDb(zipFile)
          .then(response => {
            this.working(false, '')

            let resp = response.data // {result, msg} result=0 --> ok; result != 0 --> "error msg"

            if (resp.result === '0') {
              this.databaseTransfered = true
              this.snack('success', this.$t(resp.msg))
            } else {
              this.database.filename = ''
              this.snack('error', this.$t(resp.msg))
            }
          })
          .catch(error => {
            this.working(false, '')
            this.snack('error', error.message)
          })
      }
    },
    onITouchBackupFilePicked (e) {
      const files = e.target.files
      if (files.length === 0) return

      if (files[0] !== undefined) {
        if (/^temp_data_\d{4}-\d{2}-\d{2}.{0,}\.zip$/.test(files[0].name) === false) {
          this.snack('error', this.$t('Invalid file format'))
          return
        }

        this.working(true, 'Uploading backup to restore in progress...')
        this.itouch.filename = files[0].name

        // if you want to upload multiple files at once loop
        // through the array of files
        let zipFile = new FormData()
        zipFile.append('file', files[0])

        this.Maglink.UploadBackupConfig(zipFile)
          .then(response => {
            this.working(false, '')

            let resp = response.data // {result, msg} result=0 --> ok; result != 0 --> "error msg"

            if (resp.result === '0') {
              this.configTransfered = true
              this.snack('success', this.$t(resp.msg))
            } else {
              this.itouch.filename = ''
              this.snack('error', this.$t(resp.msg))
            }
          })
          .catch(error => {
            this.working(false, '')
            this.snack('error', error.message)
          })
      }
    },

    // Log Methods
    doDownloadLogFiles () {
      let filename = this.log.file.name
      if (filename === '') return

      this.working(true, 'Log file download in progress...')

      this.Maglink.LogDownload(filename)
        .then(response => {
          this.working(false, '')

          let fileContent = response.data
          const link = this.Maglink.GetDownloadLinkBinary(fileContent, filename)
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    loadLogList () {
      this.working(true, 'Loading log file list in progress...')

      this.Maglink.GetLogList()
        .then(response => {
          this.working(false, '')
          let resp = response.data

          if (resp.result === '0') {
            this.log.available = resp.filenames
          } else {
            this.snack('error', this.$t(resp.msg))
          }
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    doPickLoglevelFile () {
      this.$refs.loglevelFile.click()
    },
    onLoglevelFilePicked (e) {
      const files = e.target.files
      if (files.length === 0) return

      if (files[0] !== undefined) {
        if (/^DebugLevel.{0,}\.txt$/.test(files[0].name) === false) {
          this.snack('error', this.$t('Invalid file format'))
          return
        }

        this.working(true, 'Setting log level...')
        this.loglevel.filename = files[0].name

        let levelFile = new FormData()
        levelFile.append('file', files[0])

        this.Maglink.UploadLogLevel(levelFile)
          .then(response => {
            this.working(false, '')

            let resp = response.data // {result, msg} result=0 --> ok; result != 0 --> "error msg"

            if (resp.result === '0') {
              this.snack('success', this.$t(resp.msg))
            } else {
              this.loglevel.filename = ''
              this.snack('error', this.$t(resp.msg))
            }
          })
          .catch(error => {
            this.working(false, '')
            this.snack('error', error.message)
          })
      }
    },

    // Reset Methods
    ConsoleReset (resetType) {
      this.dialogYN.title = this.$t('Warning').toUpperCase()
      if (resetType === 'factory') {
        this.dialogYN.labelText = '\n\n' + this.$t('You are about to RESET THE CONSOLE') + '\n\n' + this.$t('Factory defaults will be restored all data will be lost!').toUpperCase() + '\n\n\t\t\t\t' + this.$t('Are you sure?')
      } else if (resetType === 'swap') {
        this.dialogYN.labelText = '\n\n' + this.$t('You are clearing cache files') + '\n\n' + this.$t('Some data may be lost and the console will reboot') + '\n\n' + this.$t('Are you sure?')
      } else {
        this.dialogYN.labelText = '\n\n' + this.$t('You are about to RESET THE CONSOLE') + '\n\n' + this.$t('Are you sure?')
      }
      this.dialogYN.componentKey = resetType
      this.dialogYN.action = resetType
      this.dialogYN.result = resetType
      this.dialogYN.show = true
    },
    doColdReset () {
      this.working(true, 'COLD RESET in progress...')

      this.Maglink.ResetConsole('factory')
        .then(response => {
          this.working(false, '')

          let resp = response.data
          this.snack(resp.result === '0' ? 'success' : 'error', this.$t(resp.msg))
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    doSwapReset () {
      this.working(true, 'CLEAR CACHE in progress...')

      this.Maglink.ResetConsole('swap')
        .then(response => {
          this.working(false, '')

          let resp = response.data
          this.snack(resp.result === '0' ? 'success' : 'error', this.$t(resp.msg))
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    doSoftReset () {
      this.working(true, 'SOFT RESET in progress...')

      this.Maglink.ResetConsole('soft')
        .then(response => {
          this.working(false, '')

          let resp = response.data
          this.snack(resp.result === '0' ? 'success' : 'error', this.$t(resp.msg))
        })
        .catch(error => {
          this.working(false, '')
          this.snack('error', error.message)
        })
    },
    onCsldResetDialog () {
      this.filters.all = false
      this.filters.number = 1
      this.csldResetDialog = true
    },
    onCloseCsldResetDialog (status) {
      if (status === true) {
        this.Maglink.ResetCsldData(this.filters)
          .then(response => {
            this.snack('success', 'CSLD data cleared')
          })
          .catch(error => (this.snack('error', error.message)))
      }
      this.csldResetDialog = false
    },
    snack (color, message) {
      let msg = this.title + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message: msg, show: true })
    }
  }
}
</script>
